import { node, icon } from "~/util/dom";
import { CONFIG } from "~/config";
import { REVIEW_DEBUG } from "~/review/const";
import { _ } from "~/i18n";

const ERRORS = {
	NONE: 0,
	// obecna chyba
	ERROR: 1,
	EXT: 2,
	SIZE: 3,
	MAX_PHOTOS: 4,
	UPLOAD: 5,
};

enum EErrors {
	NONE = 0,
	ERROR = 1,
	EXT = 2,
	SIZE = 3,
	MAX_PHOTOS = 4,
	UPLOAD = 5,
}

const DEF_MEDIA_ID = -1;
const ADD_FILE_OUTPUT = {
	mediaId: DEF_MEDIA_ID,
	destroy: () => { /**/ },
	error: ERRORS.NONE,
	src: "",
};

interface IUploadPhotoOutput {
	mediaId: number;
}

interface IAddFileOutput {
	mediaId: number;
	src: string;
	destroy: () => void;
	error: EErrors;
}

function createAddBtn(onChange: (files: Array<File>) => void): {
	container: HTMLElement;
	changeBtn: (hasPhotos: boolean) => void;
} {
	const addBtn = node("label", { className: "file-label" });
	const fileInput = node("input", { type: "file", multiple: true });

	fileInput.addEventListener("change", () => {
		const files = Array.from(fileInput.files);

		// zmena
		onChange(files);

		// reset
		fileInput.value = "";
	});
	addBtn.addEventListener("dragover", event => {
		event.preventDefault();
		addBtn.classList.add("drag-over");
	});
	addBtn.addEventListener("dragenter", () => {
		addBtn.classList.add("drag-over");
	});
	addBtn.addEventListener("dragleave", () => {
		addBtn.classList.remove("drag-over");
	});
	addBtn.addEventListener("drop", event => {
		event.preventDefault();
		event.stopPropagation();

		addBtn.classList.remove("drag-over");

		if (event.dataTransfer?.files) {
			onChange(Array.from(event.dataTransfer?.files));
		}
	});
	const caption = node("span", { textContent: _("gallery.uploadPhoto"), className: "caption" });
	const addIcon = icon("image-round");

	addBtn.append(fileInput, addIcon, caption);

	function changeBtn(hasPhotos: boolean) {
		caption.textContent = hasPhotos ? _("add") : _("gallery.uploadPhoto");

		if (hasPhotos) {
			addBtn.classList.remove("no-photos");
		} else {
			addBtn.classList.add("no-photos");
		}
	}

	changeBtn(false);

	return {
		container: addBtn,
		changeBtn,
	};
}

type TProgressCallback = (perc: number) => void;

function createImageItem(image: HTMLImageElement|HTMLCanvasElement, rejected: boolean, onRemove: () => void): {
	container: HTMLElement;
	progress: TProgressCallback;
} {
	const container = node("span", { className: "image-item" });
	const deleteBtn = node("button", { type: "button", className: "delete-btn" });

	deleteBtn.setAttribute("data-log-ignore", "1");
	deleteBtn.append(icon("delete"));
	deleteBtn.addEventListener("click", event => {
		event.stopPropagation();
		event.preventDefault();

		onRemove();
	});

	const overlay = node("span", { className: "overlay" });
	const imageCont = node("span", { className: "image-cont" });

	imageCont.append(image);
	container.append(deleteBtn, overlay, imageCont);

	if (rejected) {
		overlay.classList.add("rejected");
		const warnIcon = icon("warn-filled");

		container.append(warnIcon);
	}

	return {
		container,
		progress: (perc: number) => {
			if (!rejected) {
				overlay.style.left = `${perc}%`;
			}
		},
	};
}

function uploadPhotoFake(progress: TProgressCallback, errorRate = 0): Promise<IUploadPhotoOutput> {
	return new Promise((resolve, reject) => {
		let ind = 0;
		const max = 25;

		let intId = setInterval(() => {
			const percent = Math.floor(ind / max * 100);

			progress(percent);
			ind++;

			if (errorRate > 0 && Math.random() < errorRate) {
				clearInterval(intId);
				intId = null;
				reject({});

				return;
			}

			if (ind > max) {
				clearInterval(intId);
				intId = null;

				resolve({
					mediaId: Math.random() * 10e6 >>> 0,
				});
			}
		}, 100);
	});
}

function uploadPhotoBe(file: File, progress: (percent: number) => void): Promise<IUploadPhotoOutput> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();

		xhr.open("PUT", `${CONFIG.requestUrl}/mediarpc/v1/reviews`, true);
		xhr.withCredentials = true;
		xhr.responseType = "json";
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				const { mediaId } = xhr.response;

				resolve({
					mediaId,
				});
			} else {
				reject({});
			}
		};
		xhr.onerror = error => {
			reject(error);
		};
		// behem uploadu updatujeme progress indikator
		xhr.upload.onprogress = (event: ProgressEvent) => {
			const { loaded, total } = event;
			const percent = Math.min(Math.floor(loaded / total * 100), 100);

			progress(percent);
		};
		xhr.send(file);
	});
}

function uploadPhoto(file: File, progress: () => void): Promise<IUploadPhotoOutput> {
	return REVIEW_DEBUG
		? uploadPhotoFake(progress, 0.01)
		: uploadPhotoBe(file, progress);
}

interface ICanvasFromImg {
	image: HTMLImageElement;
	canvasPreviewSize?: number;
}

function canvasFromImg({
	image = null,
	canvasPreviewSize = 100,
}: ICanvasFromImg): HTMLCanvasElement {
	const width = image.naturalWidth;
	const height = image.naturalHeight;
	let canWidth = 0;
	let canHeight = 0;

	if (width > height) {
		canHeight = canvasPreviewSize;
		canWidth = Math.floor(width / height * canvasPreviewSize);
	} else {
		canWidth = canvasPreviewSize;
		canHeight = Math.floor(height / width * canvasPreviewSize);
	}

	// vytvorime canvas
	const canvas = node("canvas");

	canvas.width = canWidth;
	canvas.height = canHeight;
	const ctx = canvas.getContext("2d");

	ctx.drawImage(image, 0, 0, width, height, 0, 0, canWidth, canHeight);

	return canvas;
}

interface IAddFile {
	file: File;
	minWidth: number;
	minHeight: number;
	mimeTypes: Array<string>;
	container: HTMLElement;
	onRemove: (data: IAddFileOutput) => void;
	onUpload: () => void;
}

function addFile({
	file = null,
	minWidth = 0,
	minHeight = 0,
	mimeTypes = [],
	container = null,
	onRemove = () => { /**/ },
	onUpload = () => { /**/ },
}: IAddFile): Promise<IAddFileOutput> {
	return new Promise(resolve => {
		const output: IAddFileOutput = {
			...ADD_FILE_OUTPUT,
		};
		let revokeUrl = "";
		let image = null;

		function destroy() {
			if (revokeUrl) {
				// kdyz je predavame dal, tak je uz nemuzeme bohuzel jen tak cistit z pameti - musime spolehnout na garbage collector
				//URL.revokeObjectURL(revokeUrl);
			}

			image?.container.remove();
			onRemove(output);
		}

		// bind do vystupu
		output.destroy = destroy;

		// test na koncovku
		if (!mimeTypes.includes(file.type)) {
			output.error = ERRORS.EXT;

			resolve(output);

			return;
		}

		try {
			// url obrazku
			revokeUrl = URL.createObjectURL(file);
			// obrazek
			const coverImage = new Image();

			coverImage.addEventListener("load", () => {
				const width = coverImage.naturalWidth;
				const height = coverImage.naturalHeight;

				if (width < minWidth || height < minHeight) {
					destroy();
					// nastavime chybu
					output.error = ERRORS.SIZE;
				} else {
					output.src = revokeUrl;
					const canvas = canvasFromImg({
						image: coverImage,
					});

					// uvolnime pamet
					// kdyz je predavame dal, tak je uz nemuzeme bohuzel jen tak cistit z pameti - musime spolehnout na garbage collector
					//URL.revokeObjectURL(revokeUrl);
					revokeUrl = "";

					// vytvorime image item
					image = createImageItem(canvas, false, () => {
						destroy();
					});

					container.append(image.container);

					// spustime upload
					uploadPhoto(file, image.progress).then(data => {
						output.mediaId = data.mediaId;

						onUpload();
					}, () => {
						// nastavime chybu
						output.error = ERRORS.UPLOAD;
						destroy();
					});
				}

				resolve(output);
			});
			coverImage.addEventListener("error", () => {
				destroy();

				// obecna chyba
				output.error = ERRORS.ERROR;

				resolve(output);
			});
			coverImage.src = revokeUrl;
			coverImage.crossOrigin = "anonymous";
		} catch (exc) {
			destroy();
			// obecna chyba
			output.error = ERRORS.ERROR;

			resolve(output);
		}
	});
}

function addImage(mediaId: number, imgSrc: string, rejected: boolean, container: HTMLElement, onRemove: (data: IAddFileOutput) => void): Promise<IAddFileOutput> {
	const coverImage = new Image();

	coverImage.src = imgSrc;
	coverImage.crossOrigin = "anonymous";
	let image = null;
	const output = {
		...ADD_FILE_OUTPUT,
		mediaId,
		destroy: () => {
			image.container.remove();
		},
		src: imgSrc,
	};

	image = createImageItem(coverImage, rejected, () => {
		image.container.remove();
		onRemove(output);
	});
	image.progress(100);
	container.append(image.container);

	return Promise.resolve(output);
}

export interface IImage {
	mediaId: number;
	src: string;
	rejected: boolean;
}

type TUpdate = "add" | "upload-file" | "upload-all" | "remove" | "add-files";

interface ICreateReviewPhotosUpload {
	fivePerRow?: boolean;
	maxPhotos?: number;
	minWidth?: number;
	minHeight?: number;
	mimeTypes?: Array<string>;
	onUpdate?: (data: { count: number; loading: boolean; type: TUpdate }) => void;
	onError?: (data: { msg: string }) => void;
	hidden?: boolean;
}

export function createReviewPhotosUpload({
	fivePerRow = false,
	maxPhotos = 20,
	minWidth = 800,
	minHeight = 600,
	mimeTypes = Mapy.ImageUploader.MIME_TYPES,
	onUpdate = () => { /**/ },
	onError = () => { /**/ },
	hidden = false,
}: ICreateReviewPhotosUpload): {
	container: HTMLDivElement;
	destroy: () => void;
	addPhotos: (photos: Array<IImage>, rewriteMode?: boolean) => Promise<any>;
	getMediaIds: () => Array<number>;
	getIImages: () => Array<IImage>;
	isReady: () => boolean;
	isEmpty: () => boolean;
} {
	const container = node("div", {
		// popup 5, editace 4
		className: [
			"review-photos-upload",
			...fivePerRow ? ["five-per-row"] : [],
			...hidden ? ["hidden"] : [],
		].join(" "),
	});
	let addBtnData = null;
	let list: Array<IAddFileOutput> = [];

	function isReady() {
		return list.filter(item => item.mediaId === DEF_MEDIA_ID).length === 0;
	}

	function sendUpdate(type: TUpdate) {
		const count = list.length;

		if (count === maxPhotos) {
			container.classList.add("max-photos");
		} else {
			container.classList.remove("max-photos");
		}

		addBtnData?.changeBtn(count > 0);
		onUpdate({
			count,
			loading: !isReady(),
			type,
		});
	}

	function updateError(msg: string) {
		onError({
			msg,
		});
	}

	function onRemove(data: IAddFileOutput) {
		// smazani polozky
		const index = list.indexOf(data);

		if (index !== -1) {
			list.splice(index, 1);
		}

		sendUpdate("remove");

		if (data.error === ERRORS.UPLOAD) {
			updateError(_("firmPhotos.error.photoUpload"));
		}
	}

	function destroy() {
		list.forEach(item => item.destroy());
		list.length = 0;
	}

	function getMediaIds() {
		return list.filter(item => item.mediaId !== DEF_MEDIA_ID).map(item => item.mediaId);
	}

	function getIImages(): Array<IImage> {
		return list.filter(item => item.mediaId !== DEF_MEDIA_ID).map(item => ({
			mediaId: item.mediaId,
			src: item.src,
			rejected: false,
		}));
	}

	function isEmpty() {
		return list.length === 0;
	}

	function removeItem(mediaId: number) {
		// smazani polozky
		list = list.filter(item => item.mediaId !== mediaId);

		sendUpdate("remove");
	}

	async function addPhotos(photos: Array<IImage>, rewriteMode?: boolean) {
		if (rewriteMode) {
			destroy();
		}

		// nacteni z dat
		const maxCount = maxPhotos - list.length;
		const items = photos.slice(0, maxCount);

		/* eslint-disable no-await-in-loop */
		for (let ind = 0, max = items.length; ind < max; ind++) {
			try {
				const image = items[ind];
				const fileOutput = await addImage(image.mediaId, image.src, image.rejected, container, () => {
					removeItem(image.mediaId);
				});

				list.push(fileOutput);
			} catch (exc) {
				/* eslint-disable no-console */
				console.log(exc);
			}
		}

		sendUpdate("add");
	}

	async function addFiles(allFiles: Array<File>) {
		const errorsList = [];

		sendUpdate("add-files");

		/* eslint-disable no-await-in-loop */
		for (let ind = 0, max = allFiles.length; ind < max; ind++) {
			const file = allFiles[ind];

			if (list.length === maxPhotos) {
				errorsList.push(ERRORS.MAX_PHOTOS);
				break;
			}

			try {
				const fileOutput = await addFile({ file, mimeTypes, minWidth, minHeight, container, onRemove, onUpload: () => sendUpdate("upload-file") });

				if (fileOutput.error === ERRORS.NONE) {
					list.push(fileOutput);
				} else {
					errorsList.push(fileOutput.error);
				}
			} catch (exc) {
				/* eslint-disable no-console */
				console.log(exc);
			}
		}

		sendUpdate("upload-all");

		const uniqueErrors = [...new Set(errorsList)];

		// chybove hlasky
		if (uniqueErrors.length) {
			let errorMsg = _("firmPhotos.error.photoUpload");

			if (uniqueErrors.length === 1) {
				switch (uniqueErrors[0]) {
					case ERRORS.EXT:
						errorMsg = _("firmPhotos.error.extension");
						break;
					case ERRORS.SIZE:
						errorMsg = _("firmPhotos.error.size");
						break;
					case ERRORS.MAX_PHOTOS:
						errorMsg = _("firmPhotos.error.maxCount");
						break;
					default:
				}
			}

			updateError(errorMsg);
		}
	}

	addBtnData = createAddBtn(addFiles);
	container.append(addBtnData.container);

	return {
		container,
		destroy,
		getMediaIds,
		isReady,
		isEmpty,
		addPhotos,
		getIImages,
	};
}
